<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { uploadMedia } from '@/js/core/utils/trays-api';
import {
  COMMENT_MEDIA_ACCEPT,
  MAX_UPLOAD_SIZE,
  MAX_UPLOAD_SIZE_LABEL,
  SUPPORTED_MEDIA_TYPES,
} from '@/js/core/constants/media-upload';
import { useFileDrop } from '@/js/core/composables/useFileDrop';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const items = computed(() => props.modelValue || []);
const pendingItems = ref([]);
const uploadError = ref('');
const fileInputKey = ref(0);
const metadataErrors = ref({});
const instanceId = `comment-media-${Math.random().toString(36).slice(2)}`;

const isUploading = computed(() => pendingItems.value.length > 0);

const allItems = computed(() => [
  ...items.value.map(item => ({ ...item, pending: false })),
  ...pendingItems.value.map(item => ({ ...item, pending: true })),
]);

const sizeNoteId = `${instanceId}-size-note`;
const altInputId = index => `${instanceId}-alt-${index}`;
const captionInputId = index => `${instanceId}-caption-${index}`;
const metadataErrorId = index => `${instanceId}-alt-error-${index}`;
const removeButtonId = index => `${instanceId}-remove-${index}`;

// Browser MIME is only a first-paint hint for the pending preview; the server's
// media_type replaces it once the upload resolves.
const mediaTypeFromBrowser = type => {
  if (typeof type !== 'string') return 'file';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('audio/')) return 'audio';
  if (type.startsWith('video/')) return 'video';
  if (type === 'application/pdf') return 'pdf';
  return 'file';
};

const previewType = item =>
  [...SUPPORTED_MEDIA_TYPES, 'unsupported'].includes(item.media_type) ? item.media_type : 'file';

const isUnsupported = item => item.media_type === 'unsupported';
const isImage = item => item.media_type === 'image';

const rendersAsImage = item =>
  previewType(item) === 'image' || (previewType(item) === 'pdf' && Boolean(item.display?.url));
const metadataLabel = item => (isImage(item) ? 'Alt text' : 'Description');

const clearMetadataError = index => {
  if (!metadataErrors.value[index]) return;

  const nextErrors = { ...metadataErrors.value };
  delete nextErrors[index];
  metadataErrors.value = nextErrors;
};

const preloadImage = url =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  });

const processFile = async file => {
  if (file.size > MAX_UPLOAD_SIZE) {
    uploadError.value = `File is too large. Maximum size is ${MAX_UPLOAD_SIZE_LABEL}.`;
    return;
  }

  uploadError.value = '';
  const tempId = Date.now() + Math.random();
  const previewUrl = URL.createObjectURL(file);

  pendingItems.value.push({
    tempId,
    url: previewUrl,
    alt: '',
    caption: '',
    name: file.name,
    media_type: mediaTypeFromBrowser(file.type),
  });

  try {
    const response = await uploadMedia(file);
    const result = response.data;

    // Preload items with an image thumbnail so the swap is seamless; players stream.
    if (result.media_type === 'image') {
      await preloadImage(result.display?.url || result.url);
    } else if (result.display?.url) {
      await preloadImage(result.display.url);
    }

    const pending = pendingItems.value.find(p => p.tempId === tempId);
    const alt = pending?.alt || '';
    const caption = pending?.caption || '';

    pendingItems.value = pendingItems.value.filter(p => p.tempId !== tempId);
    URL.revokeObjectURL(previewUrl);

    emit('update:modelValue', [
      ...items.value,
      {
        id: result.attachment_id,
        url: result.url,
        alt,
        caption,
        media_type: result.media_type,
        mime_type: result.mime_type,
        display: result.display || null,
      },
    ]);
  } catch (err) {
    URL.revokeObjectURL(previewUrl);
    pendingItems.value = pendingItems.value.filter(p => p.tempId !== tempId);
    uploadError.value = err.message || 'Upload failed. Please try again.';
  }

  fileInputKey.value++;
};

const { handleFileChange, handleFileDrop, handleDragOver } = useFileDrop(processFile, {
  multiple: true,
});

const updateItem = (index, field, value) => {
  const completedCount = items.value.length;

  if (index < completedCount) {
    const updated = items.value.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    emit('update:modelValue', updated);
  } else {
    const pendingIndex = index - completedCount;
    pendingItems.value[pendingIndex] = { ...pendingItems.value[pendingIndex], [field]: value };
  }

  if (field === 'alt' && value.trim()) {
    clearMetadataError(index);
  }
};

const removeItem = index => {
  metadataErrors.value = {};
  const completedCount = items.value.length;

  if (index < completedCount) {
    emit(
      'update:modelValue',
      items.value.filter((_, i) => i !== index)
    );
  } else {
    const pendingIndex = index - completedCount;
    const item = pendingItems.value[pendingIndex];
    if (item?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url);
    }
    pendingItems.value = pendingItems.value.filter((_, i) => i !== pendingIndex);
  }
};

const validateMediaMetadata = () => {
  const errors = {};

  allItems.value.forEach((item, index) => {
    if (item.pending) {
      return;
    }

    if (isUnsupported(item)) {
      errors[index] = 'This media type is no longer supported. Remove it to continue.';
      return;
    }

    if (!String(item.alt || '').trim()) {
      errors[index] = isImage(item)
        ? 'Alt text is required for images.'
        : 'A description is required for audio, video, and PDF attachments.';
    }
  });

  metadataErrors.value = errors;

  const firstInvalid = Object.keys(errors)
    .map(Number)
    .sort((a, b) => a - b)[0];

  if (firstInvalid !== undefined) {
    nextTick(() => {
      const item = allItems.value[firstInvalid];
      const targetId =
        item && isUnsupported(item) ? removeButtonId(firstInvalid) : altInputId(firstInvalid);
      document.getElementById(targetId)?.focus();
    });
  }

  return Object.keys(errors).length === 0;
};

onBeforeUnmount(() => {
  pendingItems.value.forEach(item => {
    if (item?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url);
    }
  });
});

defineExpose({ isUploading, validateMediaMetadata });
</script>

<template>
  <div class="comment-media-upload">
    <div class="comment-media-upload__dropzone" @drop="handleFileDrop" @dragover="handleDragOver">
      <input
        :key="fileInputKey"
        aria-label="Upload comment media"
        type="file"
        class="comment-media-upload__file-input"
        :accept="COMMENT_MEDIA_ACCEPT"
        :aria-describedby="sizeNoteId"
        multiple
        @change="handleFileChange"
      />
      <span class="comment-media-upload__dropzone-label"> Drop files here or click to browse </span>
    </div>

    <p :id="sizeNoteId" class="comment-media-upload__hint">The maximum allowed file size is 2MB.</p>
    <p class="comment-media-upload__hint">
      Please follow
      <a
        href="https://accessibility.huit.harvard.edu/images-and-media"
        target="_blank"
        rel="noopener noreferrer"
        >Harvard's image accessibility guidelines</a
      >.
    </p>

    <div
      v-for="(item, index) in allItems"
      :key="item.id || item.tempId"
      class="comment-media-upload__preview"
      :class="{ 'comment-media-upload__preview--unsupported': isUnsupported(item) }"
    >
      <div
        class="comment-media-upload__media"
        :class="`comment-media-upload__media--${previewType(item)}`"
      >
        <img
          v-if="rendersAsImage(item)"
          :alt="item.alt || ''"
          :src="item.display?.url || item.url"
          class="comment-media-upload__image"
          decoding="async"
        />
        <audio
          v-else-if="previewType(item) === 'audio'"
          :src="item.url"
          class="comment-media-upload__player"
          controls
        ></audio>
        <video
          v-else-if="previewType(item) === 'video'"
          :src="item.url"
          class="comment-media-upload__player"
          controls
        ></video>
        <div v-else class="comment-media-upload__file-badge">
          <span class="comment-media-upload__file-badge-type">{{
            previewType(item) === 'pdf' ? 'PDF' : 'File'
          }}</span>
          <span v-if="item.name" class="comment-media-upload__filename">{{ item.name }}</span>
        </div>
        <p v-if="item.pending" class="comment-media-upload__status">Uploading...</p>
      </div>

      <div class="comment-media-upload__fields">
        <p v-if="isUnsupported(item)" class="comment-media-upload__error" role="alert">
          Unsupported media type. Remove this item to save your changes.
        </p>
        <div class="comment-media-upload__field">
          <label :for="altInputId(index)" class="comment-media-upload__label">
            {{ metadataLabel(item)
            }}<span v-if="!isUnsupported(item)" class="comment-media-upload__required">*</span>
          </label>
          <input
            :id="altInputId(index)"
            :value="item.alt"
            type="text"
            class="comment-media-upload__input"
            :class="{ 'comment-media-upload__input--error': metadataErrors[index] }"
            :required="!isUnsupported(item)"
            :disabled="isUnsupported(item)"
            :aria-invalid="metadataErrors[index] ? 'true' : undefined"
            :aria-describedby="metadataErrors[index] ? metadataErrorId(index) : undefined"
            @input="updateItem(index, 'alt', $event.target.value)"
          />
          <p
            v-if="metadataErrors[index]"
            :id="metadataErrorId(index)"
            class="comment-media-upload__error"
            role="alert"
          >
            {{ metadataErrors[index] }}
          </p>
        </div>
        <div class="comment-media-upload__field">
          <label :for="captionInputId(index)" class="comment-media-upload__label"> Caption </label>
          <input
            :id="captionInputId(index)"
            :value="item.caption"
            type="text"
            class="comment-media-upload__input"
            :disabled="isUnsupported(item)"
            @input="updateItem(index, 'caption', $event.target.value)"
          />
        </div>
      </div>
      <button
        :id="removeButtonId(index)"
        type="button"
        class="comment-media-upload__remove"
        :aria-label="`Remove media item ${index + 1}`"
        @click="removeItem(index)"
      >
        &times;
      </button>
    </div>

    <p v-if="uploadError" class="comment-media-upload__error" role="alert">{{ uploadError }}</p>
  </div>
</template>
